import React, { useState, useMemo } from 'react';
import {
  Home,
  Building,
  Users,
  School,
  MapPin,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  FileText,
  Phone,
  DollarSign,
  Star,
  MessageCircle
} from 'lucide-react';
import './IrelandAccommodationAssistant.css';

// City data with areas and average prices
const CITIES = {
  dublin: {
    id: 'dublin',
    name: 'Dublin',
    image: '🏙️',
    description: 'Ireland\'s capital and largest city',
    areas: [
      'City Centre', 'Drumcondra', 'Rathmines', 'Phibsborough', 
      'Stoneybatter', 'Smithfield', 'Docklands', 'Ballsbridge',
      'Donnybrook', 'Ranelagh', 'Portobello', 'The Liberties'
    ],
    avgPrices: {
      student: { min: 600, max: 1200 },
      shared: { min: 500, max: 900 },
      studio: { min: 1200, max: 2000 },
      homestay: { min: 700, max: 1100 }
    }
  },
  cork: {
    id: 'cork',
    name: 'Cork',
    image: '🏘️',
    description: 'Second largest city, vibrant student life',
    areas: [
      'City Centre', 'Douglas', 'Ballintemple', 'Blackrock',
      'Glanmire', 'Mahon', 'Bishopstown', 'CIT Area'
    ],
    avgPrices: {
      student: { min: 450, max: 800 },
      shared: { min: 400, max: 700 },
      studio: { min: 800, max: 1300 },
      homestay: { min: 550, max: 850 }
    }
  },
  galway: {
    id: 'galway',
    name: 'Galway',
    image: '🌊',
    description: 'Cultural heart of Ireland, university town',
    areas: [
      'City Centre', 'Salthill', 'Knocknacarra', 'Renmore',
      'Newcastle', 'Westside', 'Terryland', 'NUIG Area'
    ],
    avgPrices: {
      student: { min: 450, max: 850 },
      shared: { min: 400, max: 750 },
      studio: { min: 850, max: 1400 },
      homestay: { min: 550, max: 900 }
    }
  },
  limerick: {
    id: 'limerick',
    name: 'Limerick',
    image: '🏰',
    description: 'Affordable city with growing tech scene',
    areas: [
      'City Centre', 'Castletroy', 'Raheen', 'Dooradoyle',
      'Annacotty', 'UL Area', 'Corbally', 'Thomondgate'
    ],
    avgPrices: {
      student: { min: 400, max: 700 },
      shared: { min: 350, max: 600 },
      studio: { min: 700, max: 1100 },
      homestay: { min: 500, max: 800 }
    }
  }
};

// Accommodation types
const ACCOMMODATION_TYPES = {
  student: {
    id: 'student',
    name: 'Student Accommodation',
    icon: <School size={20} />,
    description: 'Purpose-built student housing with facilities',
    pros: ['All bills included', 'On-site facilities', 'Student community', 'Close to campus'],
    cons: ['Fixed contracts', 'Can be expensive', 'Limited privacy', 'Summer restrictions']
  },
  shared: {
    id: 'shared',
    name: 'Private Rental (Shared)',
    icon: <Users size={20} />,
    description: 'Share an apartment or house with others',
    pros: ['More affordable', 'Social environment', 'Flexible locations', 'Home-like feel'],
    cons: ['Housemate compatibility', 'Shared facilities', 'Bills may be extra', 'Variable quality']
  },
  studio: {
    id: 'studio',
    name: 'Private Rental (Studio/1-bed)',
    icon: <Home size={20} />,
    description: 'Self-contained apartment for solo living',
    pros: ['Complete privacy', 'Own space', 'No housemate issues', 'Flexible'],
    cons: ['Most expensive', 'Can be isolating', 'All bills separate', 'Smaller space']
  },
  homestay: {
    id: 'homestay',
    name: 'Homestay',
    icon: <Building size={20} />,
    description: 'Live with a local Irish family',
    pros: ['Cultural immersion', 'Meals often included', 'Supportive environment', 'Practice English'],
    cons: ['Less independence', 'House rules', 'May be far from college', 'Curfew possible']
  }
};

// Platform data
export const PLATFORMS = [
  {
    id: 'daft',
    name: 'Daft.ie',
    url: 'https://www.daft.ie',
    type: 'general',
    description: 'Ireland\'s largest property website',
    coverage: 'Live student listings; availability changes frequently',
    pros: ['Largest selection', 'Map search', 'Price alerts', 'Verified agents'],
    cons: ['High competition', 'Quick to go', 'Agent fees possible', 'Limited student-specific'],
    tips: ['Set up alerts immediately', 'Contact within hours of listing', 'Have deposit ready'],
    rating: 5
  },
  {
    id: 'rent',
    name: 'Rent.ie',
    url: 'https://www.rent.ie',
    type: 'general',
    description: 'Popular rental platform',
    coverage: 'Live rental listings; availability changes frequently',
    pros: ['Good search filters', 'Direct landlord options', 'Mobile app', 'Area guides'],
    cons: ['Fewer listings than Daft', 'Some outdated ads', 'Limited student focus'],
    tips: ['Check both Daft and Rent.ie', 'Filter by newest first', 'Use saved searches'],
    rating: 4
  },
  {
    id: 'myhome',
    name: 'MyHome.ie',
    url: 'https://www.myhome.ie/rentals',
    type: 'general',
    description: 'Property portal by Irish Times',
    coverage: 'Live rental listings; availability changes frequently',
    pros: ['Quality listings', 'Trusted source', 'Good photos', 'Agent verified'],
    cons: ['Fewer rentals', 'More sales focused', 'Higher price range'],
    tips: ['Good for higher budget', 'Check agent reputation', 'Quality over quantity'],
    rating: 4
  },
  {
    id: 'studentpad',
    name: 'StudentPad.ie',
    url: 'https://www.studentpad.ie',
    type: 'student',
    description: 'Dedicated student accommodation',
    coverage: 'Institution-linked off-campus listings where available',
    pros: ['Student-focused', 'University partnerships', 'Verified listings', 'Summer options'],
    cons: ['Limited cities', 'Can fill quickly', 'Less variety'],
    tips: ['Book early for September', 'Check university partnerships', 'Compare with private'],
    rating: 4
  },
  {
    id: 'homestay',
    name: 'Homestay.com',
    url: 'https://www.homestay.com/inspiration/student',
    type: 'homestay',
    description: 'Host-family and homestay accommodation for students and newcomers.',
    coverage: 'Homestay and short-term options; availability varies',
    pros: ['Homestay options', 'Airport pickup', 'Support services', 'Flexible dates'],
    cons: ['Service fees', 'Limited availability', 'Homestay focused'],
    tips: ['Good for first semester', 'Book homestay early', 'Use support services'],
    rating: 3
  },
  {
    id: 'facebook',
    name: 'Facebook Groups',
    url: 'https://www.facebook.com',
    type: 'social',
    description: 'Community rental groups',
    coverage: 'Community listings; verification required',
    pros: ['Direct contact', 'No fees', 'Quick response', 'Student groups'],
    cons: ['Scam risk', 'No verification', 'Variable quality', 'Time-consuming'],
    tips: ['Verify identity', 'Never pay without viewing', 'Join university groups'],
    rating: 3
  },
  {
    id: 'university',
    name: 'University Accommodation Portals',
    url: 'https://www.educationinireland.com/en/plan-your-study-abroad/',
    type: 'university',
    description: 'Official university housing',
    coverage: 'Official college portals and student services',
    pros: ['Guaranteed quality', 'On-campus', 'All-inclusive', 'Support services'],
    cons: ['Limited spaces', 'Early deadlines', 'Fixed contracts', 'Can be expensive'],
    tips: ['Apply as soon as accepted', 'Check deadlines carefully', 'Consider off-campus options'],
    rating: 5
  },
  {
    id: 'housinganywhere',
    name: 'HousingAnywhere',
    url: 'https://housinganywhere.com',
    type: 'marketplace',
    description: 'Furnished student rooms and apartments with city and university search pages.',
    coverage: 'Furnished student listings; availability and protection vary by listing',
    pros: ['Student-focused search', 'Move-in date filters', 'Furnished options', 'Online booking options'],
    cons: ['Service fees may apply', 'Not every listing has the same protection', 'Prices vary by city'],
    tips: ['Check cancellation and protection terms', 'Confirm bills and move-in date', 'Use the exact university or city page'],
    searchTerms: 'furnished student room apartment university',
    rating: 4
  },
  {
    id: 'studentcom',
    name: 'Student.com',
    url: 'https://www.student.com/ie/dublin',
    type: 'student',
    description: 'Purpose-built student accommodation search with map and university-area options.',
    coverage: 'PBSA and private student residences; availability changes frequently',
    pros: ['Student-only inventory', 'Map search', 'Shared and studio options', 'University-area discovery'],
    cons: ['Inventory is provider-dependent', 'Booking terms vary', 'Some options carry service fees'],
    tips: ['Compare the provider’s own website', 'Check contract length and summer terms', 'Confirm what bills are included'],
    searchTerms: 'PBSA purpose built student accommodation studio room map',
    rating: 4
  },
  {
    id: 'amber',
    name: 'AmberStudent',
    url: 'https://amberstudent.com/places/search/ireland',
    type: 'student',
    description: 'Student accommodation marketplace with city and property search for international students.',
    coverage: 'International student residences; availability changes frequently',
    pros: ['International-student orientation', 'City and property search', 'Room and studio choices', 'Booking support'],
    cons: ['Third-party marketplace', 'Terms and fees vary', 'Verify the underlying provider'],
    tips: ['Read the cancellation policy', 'Check the property’s official site', 'Do not pay outside the documented checkout process'],
    searchTerms: 'international student room studio residence Ireland',
    rating: 4
  },
  {
    id: 'hostingpower',
    name: 'Hosting Power',
    url: 'https://hostingpower.ie',
    type: 'homestay',
    description: 'Host-family accommodation option for students and newcomers who want a supported arrival.',
    coverage: 'Host-family rooms; availability and meal arrangements vary',
    pros: ['Useful before arrival', 'Host-family support', 'Potential meal arrangements', 'Cultural immersion'],
    cons: ['House rules apply', 'Less independence', 'Confirm commute and meals', 'Availability varies'],
    tips: ['Ask about house rules and meals', 'Confirm total monthly cost', 'Get the arrangement in writing before paying'],
    searchTerms: 'homestay host family room meals student',
    rating: 4
  }
];

export const filterAccommodationPlatforms = ({ query = '', type = 'all' } = {}) => {
  const normalizedQuery = query.trim().toLowerCase();
  return PLATFORMS.filter((platform) => {
    const matchesType = type === 'all' || platform.type === type;
    const searchable = [
      platform.name,
      platform.description,
      platform.type,
      platform.coverage,
      platform.searchTerms,
    ].filter(Boolean).join(' ').toLowerCase();
    return matchesType && (!normalizedQuery || searchable.includes(normalizedQuery));
  });
};

// Scam red flags
const SCAM_RED_FLAGS = [
  {
    flag: 'Request for upfront payment before viewing',
    severity: 'high',
    advice: 'Never pay any money before viewing the property in person or via video call with a verified agent.'
  },
  {
    flag: 'Significantly below market price',
    severity: 'high',
    advice: 'If it seems too good to be true, it probably is. Compare with similar listings in the area.'
  },
  {
    flag: 'Landlord claims to be abroad',
    severity: 'medium',
    advice: 'Be cautious. Request video call and verify identity. Never send money overseas.'
  },
  {
    flag: 'Pressure to decide immediately',
    severity: 'medium',
    advice: 'Legitimate landlords won\'t pressure you. Take time to verify the listing.'
  },
  {
    flag: 'No formal tenancy agreement',
    severity: 'high',
    advice: 'Always insist on a written tenancy agreement. RTB registration is required by law.'
  },
  {
    flag: 'Cash-only payments requested',
    severity: 'medium',
    advice: 'Legitimate rentals should accept bank transfers. Get receipts for all payments.'
  },
  {
    flag: 'Unable to provide proof of ownership',
    severity: 'high',
    advice: 'Ask for property registration or verify with the letting agent\'s credentials.'
  },
  {
    flag: 'Poor English or generic messages',
    severity: 'low',
    advice: 'Scammers often use templates. Be wary of copy-paste responses that don\'t address your questions.'
  }
];

// Document checklist
const DOCUMENT_CHECKLIST = [
  { id: 'passport', name: 'Valid Passport', required: true },
  { id: 'visa', name: 'Student Visa / GNIB Card', required: true },
  { id: 'letter', name: 'University Acceptance Letter', required: true },
  { id: 'bank', name: 'Bank Statements (3 months)', required: true },
  { id: 'payslip', name: 'Proof of Income/Sponsorship', required: true },
  { id: 'reference', name: 'Previous Landlord Reference', required: false },
  { id: 'guarantor', name: 'Guarantor Forms (if applicable)', required: false },
  { id: 'pps', name: 'PPS Number (if available)', required: false },
  { id: 'photos', name: 'Passport Photos', required: false },
  { id: 'deposit', name: 'Deposit Amount Ready', required: true }
];

// Viewing checklist
const VIEWING_CHECKLIST = [
  'Check water pressure and hot water',
  'Test all electrical outlets',
  'Check heating system and radiators',
  'Look for signs of damp or mold',
  'Check window seals and insulation',
  'Test internet speed if possible',
  'Check security (locks, alarms)',
  'Note included appliances',
  'Check mobile phone signal',
  'Ask about bin collection',
  'Check parking availability',
  'Ask about neighbors and noise',
  'Verify commute time to college',
  'Check nearby amenities',
  'Ask about bills average'
];

// RTB and tenant rights info
const TENANT_RIGHTS = [
  {
    title: 'Identify the arrangement',
    content: 'Private tenancies, student-specific accommodation, and digs/licence arrangements can have different protections. Confirm what you are signing before paying.'
  },
  {
    title: 'Deposit and advance rent',
    content: 'For private and student-specific accommodation, check the current statutory limits on deposits and advance rent. Get receipts and keep every payment record.'
  },
  {
    title: 'RTB registration and records',
    content: 'Where the arrangement is an RTB-registered tenancy, the landlord must register it and you should receive written terms, an inventory, and rent/deposit receipts.'
  },
  {
    title: 'National rent rules from 1 March 2026',
    content: 'Private tenancies and student-specific accommodation are subject to national rent-control rules, with important exceptions. Check the RTB guidance for the property and date.'
  },
  {
    title: 'Student-specific accommodation',
    content: 'Student-specific accommodation has rules that differ from ordinary private tenancies, including limits and rent-setting requirements. Confirm the contract length and termination terms.'
  },
  {
    title: 'Minimum standards and disputes',
    content: 'Homes must meet minimum standards. Keep evidence of problems and use the local authority or RTB dispute-resolution route that applies to your arrangement.'
  }
];

const IrelandAccommodationAssistant = () => {
  const [selectedCity, setSelectedCity] = useState('dublin');
  const [selectedArea, setSelectedArea] = useState('');
  const [accommodationType, setAccommodationType] = useState('student');
  const [budget, setBudget] = useState(800);
  const [platformSearch, setPlatformSearch] = useState('');
  const [platformTypeFilter, setPlatformTypeFilter] = useState('all');
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [showScamChecker, setShowScamChecker] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showViewing, setShowViewing] = useState(false);
  const [showRights, setShowRights] = useState(false);
  const [checkedDocs, setCheckedDocs] = useState({});
  const [checkedViewing, setCheckedViewing] = useState({});

  const city = CITIES[selectedCity];
  const accType = ACCOMMODATION_TYPES[accommodationType];

  // Filter source directory by the student's search terms and source type.
  const filteredPlatforms = useMemo(() => {
    return filterAccommodationPlatforms({ query: platformSearch, type: platformTypeFilter });
  }, [platformSearch, platformTypeFilter]);

  // Check if budget is within range
  const budgetStatus = useMemo(() => {
    const prices = city.avgPrices[accommodationType];
    if (budget < prices.min) {
      return { status: 'low', message: `Below average. Typical range: €${prices.min} - €${prices.max}` };
    } else if (budget > prices.max) {
      return { status: 'high', message: `Above average. You have more options available.` };
    }
    return { status: 'good', message: `Within typical range for ${city.name}` };
  }, [budget, city, accommodationType]);

  const toggleDoc = (id) => {
    setCheckedDocs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleViewingItem = (idx) => {
    setCheckedViewing(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const formatCurrency = (amount) => {
    return `€${amount.toLocaleString()}`;
  };

  return (
    <div className="accommodation-assistant">
      <div className="assistant-header">
        <div className="header-icon">
          <Home size={32} />
        </div>
        <h2>Ireland Accommodation Search Assistant</h2>
        <p className="assistant-subtitle">
          Find student accommodation in Ireland with price guides, platform comparisons, and safety tips
        </p>
      </div>

      <div className="assistant-body">
        {/* City Selection */}
        <section className="city-section">
          <h3>
            <MapPin size={20} />
            Select Your City
          </h3>
          <div className="city-cards">
            {Object.values(CITIES).map((c) => (
              <button
                key={c.id}
                className={`city-card ${selectedCity === c.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedCity(c.id);
                  setSelectedArea('');
                }}
                aria-pressed={selectedCity === c.id}
              >
                <span className="city-image">{c.image}</span>
                <span className="city-name">{c.name}</span>
                <span className="city-desc">{c.description}</span>
              </button>
            ))}
          </div>

          {/* Area Selection */}
          {selectedCity && (
            <div className="area-selector">
              <label>Preferred Area (Optional)</label>
              <select 
                value={selectedArea} 
                onChange={(e) => setSelectedArea(e.target.value)}
              >
                <option value="">All Areas</option>
                {city.areas.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
          )}
        </section>

        {/* Accommodation Type */}
        <section className="type-section">
          <h3>
            <Building size={20} />
            Accommodation Type
          </h3>
          <div className="type-cards">
            {Object.values(ACCOMMODATION_TYPES).map((type) => (
              <button
                key={type.id}
                className={`type-card ${accommodationType === type.id ? 'selected' : ''}`}
                onClick={() => setAccommodationType(type.id)}
                aria-pressed={accommodationType === type.id}
              >
                <span className="type-icon">{type.icon}</span>
                <span className="type-name">{type.name}</span>
                <span className="type-desc">{type.description}</span>
              </button>
            ))}
          </div>

          {/* Type Details */}
          {accType && (
            <div className="type-details">
              <div className="pros-cons">
                <div className="pros">
                  <h5>✓ Pros</h5>
                  <ul>
                    {accType.pros.map((pro, idx) => (
                      <li key={idx}>{pro}</li>
                    ))}
                  </ul>
                </div>
                <div className="cons">
                  <h5>✗ Cons</h5>
                  <ul>
                    {accType.cons.map((con, idx) => (
                      <li key={idx}>{con}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Budget Section */}
        <section className="budget-section">
          <h3>
            <DollarSign size={20} />
            Monthly Budget
          </h3>
          <div className="budget-slider-container">
            <input
              type="range"
              min="400"
              max="2000"
              step="50"
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              className="budget-slider"
            />
            <div className="budget-display">
              <span className="budget-amount">{formatCurrency(budget)}</span>
              <span className="budget-period">/month</span>
            </div>
          </div>
          <div className={`budget-status ${budgetStatus.status}`}>
            {budgetStatus.status === 'low' && <AlertTriangle size={16} />}
            {budgetStatus.status === 'good' && <CheckCircle size={16} />}
            {budgetStatus.status === 'high' && <Star size={16} />}
            {budgetStatus.message}
          </div>

          {/* Price Guide */}
          <div className="price-guide">
            <h4>Indicative monthly planning ranges in {city.name}</h4>
            <p className="price-guide-note">These are budgeting guides, not live market averages. Check the linked directories for current availability and prices.</p>
            <div className="price-grid">
              {Object.entries(city.avgPrices).map(([type, prices]) => (
                <button
                  key={type}
                  type="button"
                  className={`price-card ${accommodationType === type ? 'active' : ''}`}
                  aria-pressed={accommodationType === type}
                  onClick={() => setAccommodationType(type)}
                >
                  <span className="price-type">{ACCOMMODATION_TYPES[type].name}</span>
                  <span className="price-range">
                    {formatCurrency(prices.min)} - {formatCurrency(prices.max)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Platform Directory */}
        <section className="platforms-section">
          <h3>
            <ExternalLink size={20} />
            Accommodation Platforms
          </h3>
          <div className="platform-search-controls">
            <label htmlFor="platform-search">Search accommodation sources</label>
            <div className="platform-search-row">
              <input
                id="platform-search"
                type="search"
                value={platformSearch}
                onChange={(e) => setPlatformSearch(e.target.value)}
                placeholder="Try: furnished, homestay, studio, university"
              />
              <select
                value={platformTypeFilter}
                onChange={(e) => setPlatformTypeFilter(e.target.value)}
                aria-label="Filter accommodation source type"
              >
                <option value="all">All source types</option>
                <option value="student">Student housing</option>
                <option value="general">General rental</option>
                <option value="marketplace">Marketplace</option>
                <option value="homestay">Homestay</option>
                <option value="social">Community groups</option>
                <option value="university">University sources</option>
                <option value="official">Official guidance</option>
              </select>
            </div>
            <p className="platform-search-summary">
              Showing {filteredPlatforms.length} of {PLATFORMS.length} source directories. Use the city, area, type, and budget controls above when comparing the live options.
            </p>
          </div>
          <div className="platforms-grid">
            {filteredPlatforms.length > 0 ? filteredPlatforms.map((platform) => (
              <div 
                key={platform.id} 
                className={`platform-card ${expandedPlatform === platform.id ? 'expanded' : ''}`}
              >
                <button
                  type="button"
                  className="platform-header"
                  aria-expanded={expandedPlatform === platform.id}
                  onClick={() => setExpandedPlatform(expandedPlatform === platform.id ? null : platform.id)}
                >
                  <div className="platform-info">
                    <span className="platform-name">{platform.name}</span>
                    <span className="platform-type">{platform.type}</span>
                  </div>
                  <div className="platform-meta">
                    <span className="platform-rating">
                      {'★'.repeat(platform.rating)}{'☆'.repeat(5 - platform.rating)}
                    </span>
                    {expandedPlatform === platform.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>
                
                {expandedPlatform === platform.id && (
                  <div className="platform-details">
                    <p className="platform-description">{platform.description}</p>
                    <p className="platform-listings">
                      <strong>Coverage:</strong> {platform.coverage}
                    </p>
                    
                    <div className="platform-pros-cons">
                      <div className="platform-pros">
                        <h6>Pros</h6>
                        <ul>
                          {platform.pros.map((pro, idx) => (
                            <li key={idx}>{pro}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="platform-cons">
                        <h6>Cons</h6>
                        <ul>
                          {platform.cons.map((con, idx) => (
                            <li key={idx}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    <div className="platform-tips">
                      <h6>💡 Tips</h6>
                      <ul>
                        {platform.tips.map((tip, idx) => (
                          <li key={idx}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <a 
                      href={platform.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="platform-link"
                    >
                      Visit {platform.name} <ExternalLink size={14} />
                    </a>
                  </div>
                )}
              </div>
            )) : (
              <div className="platform-empty">
                No sources match that search. Try a broader term or choose “All source types”.
              </div>
            )}
          </div>
        </section>

        {/* Additional Costs */}
        <section className="costs-section">
          <h3>
            <DollarSign size={20} />
            Additional Costs to Consider
          </h3>
          <div className="costs-grid">
            <div className="cost-item">
              <span className="cost-label">Deposit</span>
              <span className="cost-value">1 month rent</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">First month rent</span>
              <span className="cost-value">Upfront payment</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">Utilities (if not included)</span>
              <span className="cost-value">€80-150/month</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">Internet</span>
              <span className="cost-value">€30-60/month</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">TV License</span>
              <span className="cost-value">€160/year</span>
            </div>
            <div className="cost-item">
              <span className="cost-label">Agency fee (if applicable)</span>
              <span className="cost-value">Varies</span>
            </div>
          </div>
        </section>

        {/* Scam Checker */}
        <section className="scam-section">
          <button 
            className="section-toggle"
            aria-expanded={showScamChecker}
            onClick={() => setShowScamChecker(!showScamChecker)}
          >
            <Shield size={20} />
            Scam Protection Guide
            {showScamChecker ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showScamChecker && (
            <div className="scam-content">
              <div className="scam-intro">
                <AlertTriangle size={24} />
                <p>
                  Unfortunately, rental scams are common in Ireland. Protect yourself by knowing the red flags.
                </p>
              </div>
              
              <div className="red-flags">
                {SCAM_RED_FLAGS.map((item, idx) => (
                  <div key={idx} className={`red-flag severity-${item.severity}`}>
                    <div className="flag-header">
                      <AlertTriangle size={16} />
                      <span className="flag-text">{item.flag}</span>
                      <span className={`severity-badge ${item.severity}`}>
                        {item.severity}
                      </span>
                    </div>
                    <p className="flag-advice">{item.advice}</p>
                  </div>
                ))}
              </div>

              <div className="safe-payment">
                <h5>✓ Safe Payment Methods</h5>
                <ul>
                  <li>Bank transfer to verified Irish account</li>
                  <li>Credit card (offers protection)</li>
                  <li>Payment through verified platform</li>
                  <li>Get written receipt for all payments</li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Document Checklist */}
        <section className="checklist-section">
          <button 
            className="section-toggle"
            aria-expanded={showDocuments}
            onClick={() => setShowDocuments(!showDocuments)}
          >
            <FileText size={20} />
            Document Checklist
            {showDocuments ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showDocuments && (
            <div className="checklist-content">
              <p className="checklist-intro">
                Have these documents ready when applying for accommodation:
              </p>
              <div className="checklist-items">
                {DOCUMENT_CHECKLIST.map((doc) => (
                  <label key={doc.id} className={`checklist-item ${checkedDocs[doc.id] ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checkedDocs[doc.id] || false}
                      onChange={() => toggleDoc(doc.id)}
                    />
                    <span className="checkmark"></span>
                    <span className="item-name">
                      {doc.name}
                      {doc.required && <span className="required-badge">Required</span>}
                    </span>
                  </label>
                ))}
              </div>
              <div className="checklist-progress">
                <div 
                  className="accommodation-progress-bar"
                  style={{ 
                    width: `${(Object.values(checkedDocs).filter(Boolean).length / DOCUMENT_CHECKLIST.length) * 100}%` 
                  }}
                ></div>
                <span className="accommodation-progress-text">
                  {Object.values(checkedDocs).filter(Boolean).length} of {DOCUMENT_CHECKLIST.length} ready
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Viewing Checklist */}
        <section className="viewing-section">
          <button 
            className="section-toggle"
            aria-expanded={showViewing}
            onClick={() => setShowViewing(!showViewing)}
          >
            <Home size={20} />
            Viewing Checklist
            {showViewing ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showViewing && (
            <div className="viewing-content">
              <p className="viewing-intro">
                Things to check when viewing a property:
              </p>
              <div className="viewing-items">
                {VIEWING_CHECKLIST.map((item, idx) => (
                  <label key={idx} className={`viewing-item ${checkedViewing[idx] ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checkedViewing[idx] || false}
                      onChange={() => toggleViewingItem(idx)}
                    />
                    <span className="checkmark"></span>
                    <span className="item-text">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Tenant Rights */}
        <section className="rights-section">
          <button 
            className="section-toggle"
            aria-expanded={showRights}
            onClick={() => setShowRights(!showRights)}
          >
            <MessageCircle size={20} />
            Tenant Rights & RTB Info
            {showRights ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showRights && (
            <div className="rights-content">
              <div className="rtb-info">
                <h5>Residential Tenancies Board (RTB)</h5>
                <p>
                  The RTB is the government body that regulates private rentals in Ireland. 
                  All landlords must register with the RTB.
                </p>
                <a 
                  href="https://www.rtb.ie" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="rtb-link"
                >
                  Visit RTB.ie <ExternalLink size={14} />
                </a>
              </div>
              
              <div className="rights-list">
                {TENANT_RIGHTS.map((right, idx) => (
                  <div key={idx} className="right-item">
                    <h6>{right.title}</h6>
                    <p>{right.content}</p>
                  </div>
                ))}
              </div>

              <div className="emergency-contacts">
                <h5>
                  <Phone size={16} />
                  Emergency Contacts
                </h5>
                <div className="contacts-grid">
                  <div className="contact-item">
                    <span className="contact-name">RTB Helpline</span>
                    <span className="contact-number">0818 30 30 33</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-name">Threshold</span>
                    <span className="contact-number">1800 454 454</span>
                  </div>
                  <div className="contact-item">
                    <span className="contact-name">Garda (Police)</span>
                    <span className="contact-number">112 or 999</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Footer */}
        <div className="assistant-footer">
          <div className="disclaimer">
            <AlertTriangle size={16} />
            <p>
              This guide contains indicative planning information. Always verify listings independently,
              current rent and tenancy rules,
              and never send money without viewing the property. Report scams to the Garda.
            </p>
          </div>
          <div className="last-updated">
            Reviewed: August 2026 | Sources and availability change frequently; verify every listing before paying
          </div>
        </div>
      </div>
    </div>
  );
};

export default IrelandAccommodationAssistant;
